import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaCalendarAlt, FaImage, FaSpinner } from 'react-icons/fa';
import { API_URL } from '../../config/api';
import {
  buildFreshUrl,
  CONTENT_SYNC_KEYS,
  NO_CACHE_JSON_HEADERS,
  subscribeToContentUpdates,
} from '../../utils/contentSync';
import {
  getRichTextExcerpt,
  getRichTextPlainText,
  sanitizeRichTextHtml,
} from '../../utils/richText';
import './NewsBlogs.css';

const formatNewsDate = (value = '') => {
  if (!value) {
    return '';
  }

  const parsedDate = new Date(`${value}T00:00:00`);

  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(parsedDate);
};

const normalizeNewsRecord = (record = {}) => ({
  id: record.id ?? record._id ?? '',
  title: record.title || '',
  description: record.description || record.content || record.shortDescription || '',
  imageUrl: record.imageUrl || record.image_url || '',
  sortOrder: Number(record.sortOrder ?? record.sort_order ?? 0),
  date: record.date || '',
  createdAt: record.createdAt || record.created_at || '',
});

const compareNewsItems = (left, right) => {
  if ((right.sortOrder || 0) !== (left.sortOrder || 0)) {
    return (right.sortOrder || 0) - (left.sortOrder || 0);
  }

  const rightDate = right.date ? new Date(`${right.date}T00:00:00`).getTime() : 0;
  const leftDate = left.date ? new Date(`${left.date}T00:00:00`).getTime() : 0;

  if (rightDate !== leftDate) {
    return rightDate - leftDate;
  }

  const rightCreated = right.createdAt ? new Date(right.createdAt).getTime() : 0;
  const leftCreated = left.createdAt ? new Date(left.createdAt).getTime() : 0;

  if (rightCreated !== leftCreated) {
    return rightCreated - leftCreated;
  }

  return (Number(right.id) || 0) - (Number(left.id) || 0);
};

const getPlainNewsText = (value = '') => getRichTextPlainText(value).replace(/\s+/g, ' ').trim();

const NewsBlogs = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchNewsItems = async () => {
    try {
      setLoading((current) => current && items.length === 0);
      setError('');

      const response = await fetch(buildFreshUrl(API_URL.NEWS), {
        cache: 'no-store',
        headers: {
          ...NO_CACHE_JSON_HEADERS,
        },
      });
      const data = await response.json().catch(() => null);

      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to load news and blogs.');
      }

      const normalizedItems = Array.isArray(data.data)
        ? data.data
            .map(normalizeNewsRecord)
            .filter((item) => item.title || item.description || item.imageUrl)
            .sort(compareNewsItems)
        : [];

      setItems(normalizedItems);
    } catch (fetchError) {
      console.error('Error loading news and blogs:', fetchError);
      setError(fetchError.message || 'Failed to load news and blogs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNewsItems();
  }, []);

  useEffect(
    () =>
      subscribeToContentUpdates({
        keys: [CONTENT_SYNC_KEYS.NEWS],
        onUpdate: fetchNewsItems,
      }),
    []
  );

  return (
    <div className="news-blogs-page">
      <div className="news-blogs-container">
        <motion.div
          className="news-blogs-header"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="news-blogs-page-title">NEWS &amp; BLOGS</h1>
          <div className="news-heading-line" />
        </motion.div>

        {loading ? (
          <div className="news-state-card">
            <FaSpinner className="spin" />
            <p>Loading the latest news and blogs...</p>
          </div>
        ) : error ? (
          <div className="news-state-card error">
            <p>{error}</p>
          </div>
        ) : items.length === 0 ? (
          <div className="news-state-card">
            <FaImage />
            <p>No news or blogs have been published yet.</p>
          </div>
        ) : (
          <div className="news-feed-list">
            {items.map((item, index) => (
              <motion.article
                key={item.id || `news-item-${index}`}
                className={`news-feed-card${item.imageUrl ? ' news-feed-card-with-image' : ''}`}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: Math.min(index * 0.06, 0.24) }}
              >
                <div className="news-feed-body">
                  {item.date && (
                    <div className="news-feed-meta">
                      <span className="news-date-chip">
                        <FaCalendarAlt />
                        {formatNewsDate(item.date)}
                      </span>
                    </div>
                  )}

                  {item.title && (
                    item.id ? (
                      <Link to={`/news-blogs/${item.id}`} className="news-feed-title-link">
                        <h2
                          className="news-feed-title"
                          dangerouslySetInnerHTML={{
                            __html: sanitizeRichTextHtml(item.title, { wrapPlainText: false }),
                          }}
                        />
                      </Link>
                    ) : (
                      <h2
                        className="news-feed-title"
                        dangerouslySetInnerHTML={{
                          __html: sanitizeRichTextHtml(item.title, { wrapPlainText: false }),
                        }}
                      />
                    )
                  )}

                  {item.description && (
                    <p className="news-feed-excerpt">{getRichTextExcerpt(item.description, 280)}</p>
                  )}

                  {item.id && (
                    <div className="news-feed-actions">
                      <Link to={`/news-blogs/${item.id}`} className="news-read-more">
                        Read More
                      </Link>
                    </div>
                  )}
                </div>

                {item.imageUrl && (
                  <div className="news-feed-image-wrap">
                    <img
                      src={item.imageUrl}
                      alt={getPlainNewsText(item.title) || 'News image'}
                      className="news-feed-image"
                    />
                  </div>
                )}
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsBlogs;
